import React from 'react';
import { OPACITY } from '../../../constants/components';
import SubmitOnceButton from '../../submitOnce';
import RemainingAttemptsBlock from '../../remainingAttemptsBlock';
import { localize } from '../../../core/localization';
import ResultsBlock from '../ResultsBlock/ResultsBlock';
import Header from '../Header/Header';
import ExpandedTocItems from '../ExpandedTocItems';
import SectionTreeNode from '../SectionTreeNode/SectionTreeNode';
import LearningObjectiveTreeNode from '../LearningObjectiveTreeNode/LearningObjectiveTreeNode';
import ExpandedTocPoolSection from '../ExpandedTocPoolSection/ExpandedTocPoolSection';

import {
  Sections,
  ExpandedTocContainer,
  ProgressMasteryScoreWrapper,
  ProgressMasteryScore,
  ProgressCaption,
  SubmitAllAnswersButtonContainer,
  TocWrapper,
  SubmitAllAnswersWithLimitedAttempts
} from './ExpandedToc.styled';

type ExpandedTocProps = {
  isExpanded: boolean;
  courseStructure: {
    title: string;
    sections: any[];
    isAttemptSubmitted: boolean;
  };
  activeSectionId: string;
  activeQuestionId: string;
  navigateToIndex(): void;
  navigateToQuestion(sectionId: string, questionId: string, isCourseOnOnePage: boolean): void;
  navigateToLearningObjective(sectionId: string, isCourseOnOnePage: boolean): void;
  submitAllAnswerOnce(section: any): void;
  navigateToResults(): void;
  isResultsActive: boolean;
  isLearningObjectiveView: boolean;
  courseProgress: number;
  masteryScore: number;
  isCourseProgress: boolean;
  affectProgressSectionsCount: number;
  questionsForPool: any;
  isQuestionPool: boolean;
  isCourseOnOnePage: boolean;
  shouldSubmitAllQuestions: boolean;
  affectProgressQuestionsCount: number;
  answeredQuestionsProgress: number;
  showSubmitButton: boolean;
  isNextPageResults: boolean;
  isCoursePassed: boolean;
  isShowContentPagesEnabled: boolean;
  hasGuidedReattempt: boolean;
  attemptedQuestionsCount: number;
};

function ExpandedTocSection({ props }: any) {
  const {
    courseStructure,
    activeSectionId,
    activeQuestionId,
    navigateToQuestion,
    navigateToLearningObjective,
    isExpanded,
    isLearningObjectiveView,
    scrollToQuestion,
    isCourseOnOnePage,
    shouldSubmitAllQuestions,
    isCourseProgress,
    isShowContentPagesEnabled,
    hasGuidedReattempt
  } = props;
  const { isAttemptSubmitted } = courseStructure;
  return courseStructure.sections.map((section: any, sectionIndex: any) => (
    <SectionTreeNode
      key={sectionIndex}
      isActive={section.id === activeSectionId}
      section={section}
      questions={section.questions}
      tabIndex={isExpanded ? 0 : -1}
      isCourseProgress={isCourseProgress}
      sectionIndex={sectionIndex}
    >
      {section.learningObjective && isShowContentPagesEnabled && (
        <LearningObjectiveTreeNode
          role="menuitem"
          tabIndex={0}
          isActive={section.id === activeSectionId && isLearningObjectiveView}
          onClick={() => navigateToLearningObjective(section.id, isCourseOnOnePage)}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              navigateToLearningObjective(section.id, isCourseOnOnePage);
            }
          }}
        />
      )}
      <ExpandedTocItems
        props={{
          section,
          activeQuestionId,
          isExpanded,
          navigateToQuestion,
          scrollToQuestion,
          isCourseOnOnePage,
          shouldSubmitAllQuestions,
          isAttemptSubmitted,
          hasGuidedReattempt
        }}
      />
    </SectionTreeNode>
  ));
}

class ExpandedToc extends React.Component<
  ExpandedTocProps,
  { shouldShowRemainingAttemptsBlock: boolean }
> {
  refContainer: any;

  constructor(props: ExpandedTocProps) {
    super(props);
    this.state = {
      shouldShowRemainingAttemptsBlock: false
    };
    this.refContainer = React.createRef();
  }

  renderSubmitOnceMasteryScoreProgress = () => {
    const {
      courseProgress,
      answeredQuestionsProgress,
      masteryScore,
      attemptedQuestionsCount,
      affectProgressQuestionsCount,
      isCoursePassed
    } = this.props;

    const { isAttemptSubmitted } = this.props.courseStructure;

    return (
      <ProgressMasteryScoreWrapper>
        <ProgressMasteryScore
          progress={isAttemptSubmitted ? courseProgress : answeredQuestionsProgress}
          progressColor={isAttemptSubmitted && isCoursePassed ? 'correctColor' : 'textColor'}
          opacity={isAttemptSubmitted && isCoursePassed ? OPACITY.PERCENT_100 : OPACITY.PERCENT_30}
          passedMark={masteryScore}
          passedMarkStyles={{
            opacity: OPACITY.PERCENT_30,
            color: 'textColor'
          }}
          forwardedRef={this.refContainer}
        />
        <ProgressCaption data-test="question-answered-count">
          {isAttemptSubmitted
            ? localize('[you complete x]', {
                percentSuccess: courseProgress
              })
            : localize('[x out of y questions answered]', {
                countAnsweredQuestions: attemptedQuestionsCount,
                countQuestions: affectProgressQuestionsCount
              })}
        </ProgressCaption>
      </ProgressMasteryScoreWrapper>
    );
  };

  setShowRemainingAttemptsBlock = (value: boolean) => {
    this.setState({ shouldShowRemainingAttemptsBlock: value });
  };

  renderSectionProgress = (courseProgress: number, masteryScore: number) => (
    <ProgressMasteryScoreWrapper>
      <ProgressMasteryScore
        progress={courseProgress}
        progressColor="correctColor"
        opacity={OPACITY.PERCENT_100}
        passedMark={masteryScore}
        passedMarkStyles={{
          opacity: OPACITY.PERCENT_30,
          color: 'textColor'
        }}
        forwardedRef={this.refContainer}
      />
      <ProgressCaption>
        {localize('[you complete x]', {
          percentSuccess: courseProgress
        })}
      </ProgressCaption>
    </ProgressMasteryScoreWrapper>
  );

  render() {
    const {
      courseStructure,
      activeSectionId,
      activeQuestionId,
      navigateToIndex,
      navigateToResults,
      navigateToQuestion,
      navigateToLearningObjective,
      isResultsActive,
      isExpanded,
      isLearningObjectiveView,
      courseProgress,
      masteryScore,
      isCourseProgress,
      affectProgressSectionsCount,
      questionsForPool,
      isQuestionPool,
      isCourseOnOnePage,
      shouldSubmitAllQuestions,
      showSubmitButton,
      isShowContentPagesEnabled,
      hasGuidedReattempt,
      attemptedQuestionsCount
    } = this.props;
    const { isAttemptSubmitted } = courseStructure;
    const { shouldShowRemainingAttemptsBlock } = this.state;

    return (
      <React.Fragment>
        <TocWrapper>
          <ExpandedTocContainer
            ref={this.refContainer}
            isExpanded={isExpanded}
            aria-hidden={!isExpanded}
            ariaLabel={`${localize('[expanded nav section]')}`}
            shouldAdjustHeight={shouldShowRemainingAttemptsBlock && showSubmitButton}
          >
            <Header
              courseTitle={courseStructure.title}
              navigateToIndex={navigateToIndex}
              tabIndex={isExpanded ? 0 : -1}
            />
            {isCourseProgress &&
              affectProgressSectionsCount > 0 &&
              (shouldSubmitAllQuestions
                ? this.renderSubmitOnceMasteryScoreProgress()
                : this.renderSectionProgress(courseProgress, masteryScore))}

            {isQuestionPool && (
              <Sections role="menubar">
                <ExpandedTocPoolSection
                  activeQuestionId={activeQuestionId}
                  navigateToQuestion={navigateToQuestion}
                  questionsForPool={questionsForPool}
                  isExpanded={isExpanded}
                  isCourseProgress={isCourseProgress}
                  isAttemptSubmitted={isAttemptSubmitted}
                  shouldSubmitAllQuestions={shouldSubmitAllQuestions}
                  hasGuidedReattempt={hasGuidedReattempt}
                  attemptedQuestionsCount={attemptedQuestionsCount}
                />
              </Sections>
            )}
            {!isQuestionPool && (
              <Sections role="menubar">
                <ExpandedTocSection
                  props={{
                    courseStructure,
                    activeSectionId,
                    activeQuestionId,
                    navigateToIndex,
                    navigateToResults,
                    navigateToQuestion,
                    navigateToLearningObjective,
                    isResultsActive,
                    isExpanded,
                    isLearningObjectiveView,
                    shouldSubmitAllQuestions,
                    isCourseOnOnePage,
                    isCourseProgress,
                    isShowContentPagesEnabled,
                    hasGuidedReattempt
                  }}
                />
              </Sections>
            )}
          </ExpandedTocContainer>
          {shouldSubmitAllQuestions && showSubmitButton && (
            <SubmitAllAnswersWithLimitedAttempts
              shouldAdjustHeight={shouldShowRemainingAttemptsBlock && showSubmitButton}
            >
              <SubmitAllAnswersButtonContainer>
                <SubmitOnceButton
                  onClick={this.submitOnce}
                  isActive={showSubmitButton}
                  tabIndex={0}
                />
              </SubmitAllAnswersButtonContainer>
              <RemainingAttemptsBlock
                isResultsPage={false}
                setShowRemainingAttemptsBlock={this.setShowRemainingAttemptsBlock}
              />
            </SubmitAllAnswersWithLimitedAttempts>
          )}
          <ResultsBlock
            ariaLabel={localize('[results]')}
            show={shouldSubmitAllQuestions ? !showSubmitButton : true}
            isActive={isResultsActive}
            navigateToResults={navigateToResults}
            tabIndex={isExpanded ? 0 : -1}
          />
        </TocWrapper>
      </React.Fragment>
    );
  }

  submitOnce = () => {
    const { courseStructure, submitAllAnswerOnce, navigateToResults } = this.props;
    submitAllAnswerOnce(courseStructure.sections);
    navigateToResults();
  };
}

export default ExpandedToc;
